/**
 * SoA approval status + actions (ISO/IEC 27001:2022 Cl. 6.1.3(d); Workflow slide 10; PRD s2.2).
 * Presentational: the parent owns the API calls. Only a Lead Advisor sees Approve / Reopen; the API
 * enforces the same rule, this only avoids offering an action that would be refused.
 */
import React, { useState } from "react";
import { Alert, Button, Input, Modal, Tag, Typography } from "antd";
import { formatDdMmYyyy } from "../utils/formatDdMmYyyy";
import {
  SOA_AI_CONFIDENCE_NOTE,
  SOA_REOPEN_MAX,
  isSoaApproved,
  soaApprovalBlockers,
  soaApprovedBannerText,
  soaPostApprovalEditsText,
  validateReopenReason,
} from "../utils/soaApproval";

const { Text } = Typography;

/**
 * @param {object}   props
 * @param {object}   props.pkg            normalised SoA package (incl. approval stamp)
 * @param {Array}    props.rows           live rows (may carry unsaved edits)
 * @param {boolean}  props.isLeadAdvisor  strict lead_advisor role
 * @param {() => Promise<{ok: boolean, message?: string, blockers?: string[]}>} props.onApprove
 * @param {(reason: string) => Promise<{ok: boolean, message?: string}>} props.onReopen
 */
export function SoaApprovalPanel({ pkg, rows, isLeadAdvisor, onApprove, onReopen }) {
  const approved = isSoaApproved(pkg);
  const generated = Boolean(pkg?.lastAiDetailsAt);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [reopenError, setReopenError] = useState(null);

  const localBlockers = soaApprovalBlockers({ ...pkg, rows });
  const reasonError = validateReopenReason(reason);
  const provenance = pkg?.lastAiDetailsProvenance;
  const staleText = soaPostApprovalEditsText(pkg);

  const doApprove = async () => {
    setBusy(true);
    try {
      const r = await onApprove();
      if (r?.ok) {
        setConfirmOpen(false);
        setServerError(null);
      } else {
        setServerError({ message: r?.message || "Approval was refused.", blockers: r?.blockers || [] });
      }
    } finally {
      setBusy(false);
    }
  };

  const doReopen = async () => {
    setTouched(true);
    if (reasonError) return;
    setBusy(true);
    try {
      const r = await onReopen(reason.trim());
      if (r?.ok) {
        setReopenOpen(false);
        setReason("");
        setTouched(false);
        setReopenError(null);
      } else {
        setReopenError(r?.message || "Reopen was refused.");
      }
    } finally {
      setBusy(false);
    }
  };

  const aiNote = generated ? (
    <div data-testid="soa-ai-note" className="mt-1 text-xs text-[color:var(--color-text-muted)]">
      <Tag color="gold">AI drafted - review</Tag>
      <span>{SOA_AI_CONFIDENCE_NOTE}</span>
      {provenance && (provenance.modelId || provenance.requestId) ? (
        <span className="ml-2 text-[color:var(--color-text-muted)]">
          Model {provenance.modelId || "n/a"}
          {provenance.promptId ? `, prompt ${provenance.promptId}` : ""}
          {provenance.requestId ? `, request ${provenance.requestId}` : ""}
        </span>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="space-y-3" data-testid="soa-approval-panel">
      {staleText ? (
        <Alert type="warning" showIcon data-testid="soa-stale-warning" message={staleText} />
      ) : null}
      {approved ? (
        <Alert
          type="success"
          showIcon
          message={<span data-testid="soa-approved-banner">{soaApprovedBannerText(pkg)}</span>}
          description={
            <div>
              <div>AI-drafted, human-approved. The Statement of Applicability is locked; Sync and Generate details are disabled.</div>
              {aiNote}
              {isLeadAdvisor ? (
                <Button className="mt-2" danger size="small" onClick={() => { setReopenError(null); setReopenOpen(true); }}>
                  Reopen for correction
                </Button>
              ) : (
                <Text type="secondary" className="mt-2 block text-xs">
                  Only the Lead Advisor can reopen an approved Statement of Applicability for correction.
                </Text>
              )}
            </div>
          }
        />
      ) : (
        <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-4">
          {pkg?.approvalStatus === "reopened_for_correction" ? (
            <Alert
              className="mb-3"
              type="warning"
              showIcon
              message={`Reopened for correction${pkg.reopenedAt ? ` on ${formatDdMmYyyy(pkg.reopenedAt)}` : ""}`}
              description={pkg.reopenReason ? `Reason: ${pkg.reopenReason}` : undefined}
            />
          ) : null}
          {generated ? (
            <>
              <Tag color="orange" data-testid="soa-awaiting-tag">Awaiting Lead Advisor approval</Tag>
              {aiNote}
            </>
          ) : (
            <Text type="secondary" className="text-xs" data-testid="soa-not-generated">
              Not ready for approval: generate the SOA details first.
            </Text>
          )}
          {isLeadAdvisor ? (
            <div className="mt-3">
              <Button
                type="primary"
                disabled={localBlockers.length > 0}
                onClick={() => { setServerError(null); setConfirmOpen(true); }}
              >
                Approve Statement of Applicability
              </Button>
              {localBlockers.length > 0 ? (
                <ul data-testid="soa-local-blockers" className="mt-2 list-disc pl-5 text-xs text-[color:var(--color-text-muted)]">
                  {localBlockers.map((b) => <li key={b}>{b}</li>)}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      <Modal
        open={confirmOpen}
        title="Approve Statement of Applicability"
        okText="Approve and lock"
        confirmLoading={busy}
        onOk={() => void doApprove()}
        onCancel={() => setConfirmOpen(false)}
      >
        <p data-testid="soa-confirm-text">
          This is a formal approval of AI-assisted content. The justification and implementation text was drafted by AI;
          by approving you confirm you have reviewed it. Approval locks the Statement of Applicability: Sync, Generate
          details and edits are disabled until you reopen it for correction.
        </p>
        {serverError ? (
          <Alert
            type="error"
            showIcon
            data-testid="soa-approve-error"
            message={serverError.message}
            description={
              serverError.blockers.length ? (
                <ul className="list-disc pl-5">{serverError.blockers.map((b) => <li key={b}>{b}</li>)}</ul>
              ) : undefined
            }
          />
        ) : null}
      </Modal>

      <Modal
        open={reopenOpen}
        title="Reopen Statement of Applicability for correction"
        okText="Reopen"
        confirmLoading={busy}
        onOk={() => void doReopen()}
        onCancel={() => setReopenOpen(false)}
      >
        <p className="text-sm">The approved version is preserved in the version history. Give the reason for reopening.</p>
        <Input.TextArea
          data-testid="soa-reopen-reason"
          rows={4}
          maxLength={SOA_REOPEN_MAX}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onBlur={() => setTouched(true)}
        />
        {touched && reasonError ? <div data-testid="soa-reopen-reason-error" className="mt-1 text-xs text-[color:var(--color-danger-fg)]">{reasonError}</div> : null}
        {reopenError ? <Alert className="mt-3" type="error" showIcon data-testid="soa-reopen-error" message={reopenError} /> : null}
      </Modal>
    </div>
  );
}

export default SoaApprovalPanel;
